import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import {
  ExclamationTriangleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { useSearchParams, useNavigate } from "react-router-dom";

import { useBot } from "../../context/botContext";
import { baseAxios } from "../../apis";
import { useToast } from "../../context/toastContext";

import { SubInfoPage } from "./SubInfoPage";
import AccountDetails from "./AccountDetails";
import ReferralTab from "./ReferralTab";
import AdsComponents from "../ads/AdsComponents";
import DialogFastConnect from "../dialogs/DialogFastConnet";
import DialogSuccess from "../dialogs/DialogSuccess";
import DrawerApiConnect from "../drawers/DrawerApiConnect";
import DrawerExchangeWallet from "../drawers/DrawerExchangeWallet";
import CardAvailableExchangeSkeleton from "../cards/skeletons/CardAvailableExchangeSkeleton";
import { WalletItem } from "../dashboard/ExchangeWallet";
import TelegramTab from "./TelegramTab";

import "swiper/css";
import "swiper/css/navigation";
import { useAuth } from "../../context/authContext";

const TABS = {
  OVERVIEW: "overview",
  EXCHANGES: "exchanges",
  SUBSCRIPTION: "subscription",
  REFERRAL: "referral",
  TELEGRAM: "telegram",
};

// --- Referrals Query Hook ---
const useReferralsQuery = (page) => {
  return useQuery({
    queryKey: ["myReferrals", page],
    queryFn: async () => {
      const { data } = await baseAxios.get("/api/my-referrals", {
        params: { page },
      });
      return data;
    },
    placeholderData: (previousData) => previousData,
  });
};

// --- ExchangesTab Component ---
const ExchangesTab = ({
  loadingExchanges,
  connectedExchanges,
  handleOpenApiConnectDrawer,
  setOpenDialog,
}) => (
  <div className="bg-white rounded-xl">
    <div className="flex flex-col mt-6">
      <h4 className="font-semibold text-xl">Connected Exchange</h4>
      <span className="block text-sm text-[#21272A]/60 font-normal">
        Connect to your available exchange wallet.
      </span>
    </div>

    <div className="w-full flex flex-col lg:flex-row lg:space-x-4 pb-2 mt-6">
      <div
        className="bg-white/80 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center border border-dashed border-gray-300 text-center cursor-pointer hover:bg-gray-100 w-full lg:max-w-[200px] h-[90px]"
        onClick={() => setOpenDialog("avail-exchange")}
      >
        <p className="text-base font-medium text-gray-700 mb-1 mt-0">
          Add More
        </p>
        <PlusCircleIcon className="w-[24px] h-[24px] text-[#007A78]" />
      </div>

      <Swiper
        modules={[Navigation]}
        spaceBetween={16}
        slidesPerView={1}
        navigation
        breakpoints={{
          0: { slidesPerView: 1.2 },
          480: { slidesPerView: 1.5 },
          640: { slidesPerView: 2 },
          768: { slidesPerView: 2.2 },
          1000: { slidesPerView: 1.2 },
          1100: { slidesPerView: 2.2 },
        }}
        className="mt-4 lg:mt-0 w-full"
      >
        <style>{`
          .swiper-button-next,
          .swiper-button-prev {
            display: none !important;
          }
        `}</style>

        {loadingExchanges
          ? [...Array(6)].map((_, idx) => (
              <SwiperSlide key={`${idx}-skeleton-available`}>
                <CardAvailableExchangeSkeleton />
              </SwiperSlide>
            ))
          : connectedExchanges?.map((ex) => (
              <SwiperSlide key={ex.id}>
                <WalletItem
                  wallet={ex}
                  onConnectApiClick={handleOpenApiConnectDrawer}
                />
              </SwiperSlide>
            ))}
      </Swiper>
    </div>
  </div>
);

export const AccountPage = () => {
  const {
    exchanges: availableExchanges,
    myExchanges: connectedExchanges,
    loadingGetExchange: loadingExchanges,
  } = useBot();

  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { subscription } = useAuth();

  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const [openDialog, setOpenDialog] = useState(null);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [page, setPage] = useState(1);

  const { data: referralData, isLoading: loadingReferrals } =
    useReferralsQuery(page);

  const handleOpenApiConnectDrawer = (wallet) => {
    setSelectedExchange(wallet);
    setOpenDialog("api-connect");
  };

  const handleCloseApiConnectDrawer = () => {
    setOpenDialog(null);
    setSelectedExchange(null);
  };

  //  Handle URL parameters (tab, subscription, etc.)
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const subscriptionStatus = searchParams.get("subscription");
    const sessionId = searchParams.get("session_id");

    // Handle "?tab=exchanges" or any other valid tab
    if (tabParam && Object.values(TABS).includes(tabParam)) {
      setActiveTab(tabParam);
      navigate("/account", { replace: true }); // optional: clean URL
      return;
    }

    // Handle subscription flow
    if (subscriptionStatus || sessionId) {
      setActiveTab(TABS.SUBSCRIPTION);
    }
  }, [searchParams, navigate]);

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.OVERVIEW:
        return <AccountDetails />;
      case TABS.EXCHANGES:
        return (
          <ExchangesTab
            loadingExchanges={loadingExchanges}
            connectedExchanges={connectedExchanges}
            handleOpenApiConnectDrawer={handleOpenApiConnectDrawer}
            setOpenDialog={setOpenDialog}
          />
        );
      case TABS.SUBSCRIPTION:
        return (
          <div className="bg-white">
            <SubInfoPage />
          </div>
        );
      case TABS.REFERRAL:
        return (
          <div className="bg-white rounded-xl">
            <ReferralTab
              referralData={referralData}
              loading={loadingReferrals}
              page={page}
              setPage={setPage}
            />
          </div>
        );
      case TABS.TELEGRAM:
        return (
          <div className="bg-white rounded-xl">
            <TelegramTab />
          </div>
        );
      default:
        return null;
    }
  };

  const renderTab = (tab, label) => (
    <span
      className={`pb-2 cursor-pointer text-sm font-medium whitespace-nowrap ${
        activeTab === tab
          ? "text-[#1C1C1C] border-b-2 border-[#1C1C1C]"
          : "text-gray-400"
      }`}
      onClick={() => setActiveTab(tab)}
    >
      {label}
    </span>
  );

  return (
    <div className="w-full">
      <h1 className="text-3xl text-left font-bold text-gray-900">Account</h1>

      <div
        className={`flex flex-col ${
          [TABS.OVERVIEW, TABS.EXCHANGES].includes(activeTab)
            ? "lg:grid lg:grid-cols-5 lg:col-span-4"
            : ""
        } gap-4 w-full`}
      >
        <div className="bg-white p-6 rounded-xl lg:col-span-4">
          <div className="flex space-x-6 border-b border-gray-200 mb-5 overflow-x-auto no-scrollbar">
            {renderTab(TABS.OVERVIEW, "Overview")}
            {renderTab(TABS.EXCHANGES, "Exchanges")}
            {renderTab(TABS.SUBSCRIPTION, "Subscription")}
            {renderTab(TABS.REFERRAL, "My Referral")}
            {renderTab(TABS.TELEGRAM, "Telegram")}
          </div>
          <div>{renderTabContent()}</div>
        </div>

        {[TABS.OVERVIEW, TABS.EXCHANGES].includes(activeTab) && (
          <div className="bg-white rounded-xl col-span-1 border border-[#dde1e6]">
            <AdsComponents />
          </div>
        )}
      </div>
      {/*  Dialogs */}
      <DialogFastConnect
        open={openDialog === "fast-connect"}
        onClose={() => setOpenDialog(null)}
      />
      <DialogSuccess
        open={openDialog === "success-connect-exchange"}
        onClose={() => setOpenDialog(null)}
        title="API connection established – your exchange wallet is now linked."
      />
      <DrawerExchangeWallet
        loading={loadingExchanges}
        isOpen={openDialog === "avail-exchange"}
        onClose={() => setOpenDialog(null)}
        wallets={availableExchanges}
        onConnectApiClick={handleOpenApiConnectDrawer}
      />
      <DrawerApiConnect
        isOpen={openDialog === "api-connect"}
        onClose={handleCloseApiConnectDrawer}
        wallet={selectedExchange}
      />
    </div>
  );
};
